import { CAST_AI } from '@cast/constants';
import {
  Card,
  ProgressBar,
  Skeleton,
  Tooltip,
  Typography,
  Stack,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

type CurrentUsageCardProps = {
  label: string;
  testId?: string;
  used?: number;
  provisioned?: number;
  unit: string;
  isLoading?: boolean;
  isCloudDisksEnabled?: boolean;
};

export const CurrentUsageCard = ({
  label,
  testId,
  used,
  provisioned,
  unit,
  isLoading,
  isCloudDisksEnabled,
}: CurrentUsageCardProps) => {
  const headroomPct =
    used !== undefined && provisioned !== undefined && provisioned > 0
      ? Math.round(((provisioned - used) / provisioned) * 100)
      : undefined;

  const barValue =
    used !== undefined && provisioned !== undefined && provisioned > 0
      ? Math.min((used / provisioned) * 100, 100)
      : 0;

  return (
    <Card
      sx={{ flex: 1, p: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
      testId={testId}
    >
      <Stack gap={4}>
        <Typography variant="P12M">{label}</Typography>
        {isLoading ? (
          <Skeleton width={120} height={24} />
        ) : (
          <Stack direction="row" alignItems="baseline" gap={4}>
            <Typography variant="h6" data-testid="current-usage-value">
              {dashesOrNum({ value: used, roundBy: 2 })}
              {isCloudDisksEnabled && '/'}
              {isCloudDisksEnabled &&
                dashesOrNum({ value: provisioned, roundBy: 2 })}
            </Typography>
            <Typography
              variant="P12R"
              color="textSecondary"
              data-testid="current-usage-unit"
            >
              {unit}
            </Typography>
          </Stack>
        )}
      </Stack>
      {isCloudDisksEnabled && (
        <Stack gap={4}>
          {isLoading ? (
            <>
              <Skeleton width="100%" height={8} />
              <Skeleton width={80} height={18} />
            </>
          ) : (
            <>
              <ProgressBar
                size="lg"
                variant="determinate"
                color="secondary"
                value={barValue}
              />
              <Tooltip
                title={`The buffer between current usage and provisioned capacity. ${CAST_AI} maintains headroom to absorb usage spikes.`}
              >
                <Typography
                  variant="P12R"
                  color="textSecondary"
                  component="span"
                >
                  {headroomPct !== undefined ? `${headroomPct}% headroom` : '—'}
                </Typography>
              </Tooltip>
            </>
          )}
        </Stack>
      )}
    </Card>
  );
};
